import { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import Navbar from '@/components/Navbar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Users, Heart, MessageCircle, Share2, Gift, Building2, 
  MapPin, Phone, Globe, Send, Plus, Loader2, Clock, Package, ChevronDown, ChevronUp, X, RefreshCcw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const Social = () => {
  const { API, user } = useAuth();
  const [loading, setLoading] = useState(false);
  
  // Social Feed
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState({ content: '', post_type: 'tip' });
  
  // Food Donations
  const [foodDonations, setFoodDonations] = useState([]);
  const [showDonationForm, setShowDonationForm] = useState(false);
  const [newDonation, setNewDonation] = useState({
    title: '',
    description: '',
    food_items: '',
    quantity: '',
    expiry_date: '',
    pickup_location: ''
  });
  
  // NGOs
  const [ngos, setNgos] = useState([]);
  
  // Comments
  const [expandedComments, setExpandedComments] = useState({});
  const [postComments, setPostComments] = useState({});
  const [newComment, setNewComment] = useState({});
  const [loadingComments, setLoadingComments] = useState({});

  useEffect(() => {
    fetchSocialFeed();
    fetchFoodDonations();
    fetchNGOs();
  }, []);

  const fetchSocialFeed = async () => {
    try {
      const response = await axios.get(`${API}/social/feed`);
      setPosts(response.data.posts || []);
    } catch (error) {
      // Show empty state instead of demo posts for better UX
      setPosts([]);
      if (error.response?.status !== 401) {
        toast.error('Nu am putut încărca postările');
      }
    }
  };

  const fetchFoodDonations = async () => {
    try {
      const response = await axios.get(`${API}/donations/food`);
      setFoodDonations(response.data.donations || []);
    } catch (error) {
      // Silently handle
    }
  };

  const fetchNGOs = async () => {
    try {
      const response = await axios.get(`${API}/ngos`);
      setNgos(response.data.ngos || []);
    } catch (error) {
      // Silently handle
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!newPost.content.trim()) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/social/posts`, newPost);
      setPosts([response.data, ...posts]);
      setNewPost({ content: '', post_type: 'tip' });
      toast.success('Postare publicată!');
    } catch (error) {
      toast.error('Eroare la publicare');
    } finally {
      setLoading(false);
    }
  };

  const handleLikePost = async (postId) => {
    try {
      const response = await axios.post(`${API}/social/posts/${postId}/like`);
      setPosts(posts.map(p => 
        p.id === postId 
          ? { ...p, is_liked: response.data.liked, likes_count: p.likes_count + (response.data.liked ? 1 : -1) }
          : p
      ));
    } catch (error) {
      toast.error('Eroare');
    }
  };

  const toggleComments = async (postId) => {
    const isExpanded = expandedComments[postId];
    
    if (!isExpanded && !postComments[postId]) {
      // Load comments if not loaded yet
      setLoadingComments({ ...loadingComments, [postId]: true });
      try {
        const response = await axios.get(`${API}/social/posts/${postId}/comments`);
        setPostComments({ ...postComments, [postId]: response.data.comments || [] });
      } catch (error) {
        console.error('Error loading comments:', error);
        setPostComments({ ...postComments, [postId]: [] });
      } finally {
        setLoadingComments({ ...loadingComments, [postId]: false });
      }
    }
    
    setExpandedComments({ ...expandedComments, [postId]: !isExpanded });
  };

  const handleAddComment = async (postId) => {
    const commentText = newComment[postId]?.trim();
    if (!commentText) return;
    
    try {
      const response = await axios.post(`${API}/social/posts/${postId}/comments`, {
        content: commentText
      });
      
      // Add new comment to the list
      setPostComments({
        ...postComments,
        [postId]: [...(postComments[postId] || []), { 
          ...response.data, 
          author_email: user?.email || 'Tu' 
        }]
      });
      
      // Update comments count
      setPosts(posts.map(p => 
        p.id === postId 
          ? { ...p, comments_count: (p.comments_count || 0) + 1 }
          : p
      ));
      
      // Clear input
      setNewComment({ ...newComment, [postId]: '' });
      toast.success('Comentariu adăugat!');
    } catch (error) {
      toast.error('Eroare la adăugarea comentariului');
    }
  };

  const handleCreateDonation = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const donationData = {
        ...newDonation,
        // Keep food_items as string, backend expects string
        food_items: newDonation.food_items.trim()
      };
      const response = await axios.post(`${API}/donations/food`, donationData);
      setFoodDonations([response.data, ...foodDonations]);
      setShowDonationForm(false);
      setNewDonation({ title: '', description: '', food_items: '', quantity: '', expiry_date: '', pickup_location: '' });
      toast.success('Donație publicată! Mulțumim pentru generozitate!');
    } catch (error) {
      toast.error('Eroare la publicare');
    } finally {
      setLoading(false);
    }
  };

  const handleClaimDonation = async (donationId) => {
    try {
      await axios.post(`${API}/donations/food/${donationId}/claim`);
      setFoodDonations(foodDonations.map(d => 
        d.id === donationId ? { ...d, status: 'claimed' } : d
      ));
      toast.success('Donație revendicată! Contactează donatorul pentru ridicare.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare');
    }
  };

  const getDemoPosts = () => [
    {
      id: 'demo-1',
      content: 'Am descoperit o rețetă minunată de supă de legume cu ingrediente de sezon! Perfectă pentru zilele reci de iarnă. 🥕🥬',
      post_type: 'tip',
      author_email: 'maria@example.com',
      likes_count: 24,
      comments_count: 5,
      is_liked: false,
      created_at: new Date().toISOString()
    },
    {
      id: 'demo-2',
      content: 'Sfat: Înlocuiți zahărul din deserturi cu miere sau sirop de arțar pentru o variantă mai sănătoasă! 🍯',
      post_type: 'tip',
      author_email: 'andrei@example.com',
      likes_count: 45,
      comments_count: 12,
      is_liked: true,
      created_at: new Date(Date.now() - 3600000).toISOString()
    }
  ];

  const postTypes = [
    { id: 'tip', label: 'Sfat', icon: '💡' },
    { id: 'recipe', label: 'Rețetă', icon: '🍳' },
    { id: 'question', label: 'Întrebare', icon: '❓' }
  ];

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      <Navbar />

      <div className="container mx-auto px-4 sm:px-6 lg:px-12 py-6 md:py-8">
        <div className="animate-fade-in">
          <div className="text-center mb-6 md:mb-8">
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light text-[#1A4D2E] mb-2">
              Comunitate
            </h1>
            <p className="text-[#4a5568] text-base md:text-lg px-4">
              Conectează-te cu alți membri, împărtășește rețete și ajută comunitatea
            </p>
          </div>

          <Tabs defaultValue="feed" className="max-w-4xl mx-auto">
            <TabsList className="grid w-full grid-cols-3 mb-6 md:mb-8 h-auto p-1">
              <TabsTrigger value="feed" className="flex-col sm:flex-row gap-1 sm:gap-2 py-2.5 text-xs sm:text-sm">
                <Users className="w-4 h-4" />
                <span className="hidden sm:inline">Feed Social</span>
                <span className="sm:hidden">Feed</span>
              </TabsTrigger>
              <TabsTrigger value="donations" className="flex-col sm:flex-row gap-1 sm:gap-2 py-2.5 text-xs sm:text-sm">
                <Gift className="w-4 h-4" />
                <span className="hidden sm:inline">Donații</span>
                <span className="sm:hidden">Donații</span>
              </TabsTrigger>
              <TabsTrigger value="ngos" className="flex-col sm:flex-row gap-1 sm:gap-2 py-2.5 text-xs sm:text-sm">
                <Building2 className="w-4 h-4" />
                <span className="hidden sm:inline">ONG-uri</span>
                <span className="sm:hidden">ONG</span>
              </TabsTrigger>
            </TabsList>

            {/* Social Feed Tab */}
            <TabsContent value="feed">
              {/* Header with Refresh */}
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-[#4a5568]">{posts.length} postări în comunitate</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={fetchSocialFeed}
                  className="border-[#1A4D2E] text-[#1A4D2E] text-xs"
                >
                  <RefreshCcw className="w-3 h-3 mr-1" />
                  Actualizează
                </Button>
              </div>
              
              {/* Create Post */}
              <Card className="border-none card-shadow mb-6">
                <CardContent className="p-4 sm:p-6">
                  <form onSubmit={handleCreatePost}>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {postTypes.map((type) => (
                        <Button
                          key={type.id}
                          type="button"
                          size="sm"
                          variant={newPost.post_type === type.id ? 'default' : 'outline'}
                          onClick={() => setNewPost({ ...newPost, post_type: type.id })}
                          className={`text-xs sm:text-sm ${newPost.post_type === type.id ? 'bg-[#1A4D2E]' : ''}`}
                        >
                          {type.icon} <span className="ml-1">{type.label}</span>
                        </Button>
                      ))}
                    </div>
                    <Textarea
                      placeholder="Împărtășește un sfat, o rețetă sau pune o întrebare..."
                      value={newPost.content}
                      onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                      className="bg-[#F5F5F0] border-transparent focus:border-[#1A4D2E] min-h-[80px] sm:min-h-[100px] mb-3 text-sm sm:text-base"
                    />
                    <div className="flex justify-end">
                      <Button 
                        type="submit" 
                        disabled={loading || !newPost.content.trim()}
                        className="bg-[#1A4D2E] text-white text-sm"
                      >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                        Publică
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>

              {/* Posts */}
              <div className="space-y-4">
                {posts.map((post) => (
                  <Card key={post.id} className="border-none card-shadow">
                    <CardContent className="p-4 sm:p-6">
                      <div className="flex items-start sm:items-center justify-between mb-3 gap-2">
                        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                          <div className="w-9 h-9 sm:w-10 sm:h-10 bg-[#1A4D2E] rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-base flex-shrink-0">
                            {post.author_email?.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-[#1A4D2E] text-sm sm:text-base truncate">{post.author_email}</p>
                            <p className="text-xs text-[#4a5568]">
                              {new Date(post.created_at).toLocaleDateString('ro-RO', { 
                                day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' 
                              })}
                            </p>
                          </div>
                        </div>
                        <Badge className="bg-[#1A4D2E]/10 text-[#1A4D2E] text-xs flex-shrink-0">
                          {postTypes.find(t => t.id === post.post_type)?.icon}
                          <span className="hidden sm:inline ml-1">{postTypes.find(t => t.id === post.post_type)?.label}</span>
                        </Badge>
                      </div>
                      
                      <p className="text-[#4a5568] mb-4 whitespace-pre-wrap text-sm sm:text-base">{post.content}</p>
                      
                      <div className="flex flex-wrap items-center gap-2 sm:gap-4 pt-3 border-t border-[#E2E8F0]">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleLikePost(post.id)}
                          className={`text-xs sm:text-sm px-2 sm:px-3 ${post.is_liked ? 'text-red-500' : 'text-[#4a5568]'}`}
                        >
                          <Heart className={`w-4 h-4 mr-1 ${post.is_liked ? 'fill-current' : ''}`} />
                          {post.likes_count}
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-[#4a5568] text-xs sm:text-sm px-2 sm:px-3"
                          onClick={() => toggleComments(post.id)}
                        >
                          <MessageCircle className="w-4 h-4 mr-1" />
                          {post.comments_count}
                          {expandedComments[post.id] ? (
                            <ChevronUp className="w-3 h-3 ml-1" />
                          ) : (
                            <ChevronDown className="w-3 h-3 ml-1" />
                          )}
                        </Button>
                        <Button variant="ghost" size="sm" className="text-[#4a5568] text-xs sm:text-sm px-2 sm:px-3 hidden sm:flex">
                          <Share2 className="w-4 h-4 mr-1" />
                          Distribuie
                        </Button>
                      </div>
                      
                      {/* Comments Section */}
                      {expandedComments[post.id] && (
                        <div className="mt-4 pt-4 border-t border-[#E2E8F0]">
                          {/* Loading comments */}
                          {loadingComments[post.id] && (
                            <div className="flex justify-center py-4">
                              <Loader2 className="w-5 h-5 animate-spin text-[#1A4D2E]" />
                            </div>
                          )}
                          
                          {/* Comments list */}
                          {!loadingComments[post.id] && postComments[post.id]?.length > 0 && (
                            <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
                              {postComments[post.id].map((comment, idx) => (
                                <div key={comment.id || idx} className="flex gap-3 bg-[#F5F5F0] p-3 rounded-lg">
                                  <div className="w-8 h-8 bg-[#1A4D2E]/20 rounded-full flex items-center justify-center text-[#1A4D2E] font-bold text-sm flex-shrink-0">
                                    {comment.author_email?.charAt(0).toUpperCase()}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="font-medium text-sm text-[#1A4D2E]">{comment.author_email}</span>
                                      <span className="text-xs text-[#4a5568]">
                                        {new Date(comment.created_at).toLocaleDateString('ro-RO', { 
                                          day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' 
                                        })}
                                      </span>
                                    </div>
                                    <p className="text-sm text-[#4a5568] break-words">{comment.content}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* No comments message */}
                          {!loadingComments[post.id] && (!postComments[post.id] || postComments[post.id].length === 0) && (
                            <p className="text-sm text-[#4a5568] text-center py-4">
                              Niciun comentariu încă. Fii primul care comentează!
                            </p>
                          )}
                          
                          {/* Add comment input */}
                          <div className="flex gap-2 mt-3">
                            <Input
                              placeholder="Scrie un comentariu..."
                              value={newComment[post.id] || ''}
                              onChange={(e) => setNewComment({ ...newComment, [post.id]: e.target.value })}
                              onKeyPress={(e) => e.key === 'Enter' && handleAddComment(post.id)}
                              className="bg-[#F5F5F0] border-transparent focus:border-[#1A4D2E] flex-1"
                            />
                            <Button 
                              size="sm"
                              onClick={() => handleAddComment(post.id)}
                              disabled={!newComment[post.id]?.trim()}
                              className="bg-[#1A4D2E] text-white"
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Food Donations Tab */}
            <TabsContent value="donations">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
                <div>
                  <h2 className="font-serif text-xl sm:text-2xl text-[#1A4D2E]">Donații de Alimente</h2>
                  <p className="text-[#4a5568] text-sm sm:text-base">Oferă sau primește alimente de la comunitate</p>
                </div>
                <Button 
                  onClick={() => setShowDonationForm(!showDonationForm)}
                  className="bg-[#1A4D2E] text-white w-full sm:w-auto"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Donează Alimente
                </Button>
              </div>

              {/* Donation Form */}
              {showDonationForm && (
                <Card className="border-none card-shadow mb-6">
                  <CardHeader className="pb-2 sm:pb-4">
                    <CardTitle className="text-[#1A4D2E] text-lg sm:text-xl">Creează o Donație</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
                    <form onSubmit={handleCreateDonation} className="space-y-3 sm:space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <Input
                          placeholder="Titlu (ex: Legume proaspete)"
                          value={newDonation.title}
                          onChange={(e) => setNewDonation({ ...newDonation, title: e.target.value })}
                          required
                          className="bg-[#F5F5F0] text-sm"
                        />
                        <Input
                          placeholder="Cantitate (ex: 5kg, 10 buc)"
                          value={newDonation.quantity}
                          onChange={(e) => setNewDonation({ ...newDonation, quantity: e.target.value })}
                          className="bg-[#F5F5F0] text-sm"
                        />
                      </div>
                      <Input
                        placeholder="Produse (separate prin virgulă)"
                        value={newDonation.food_items}
                        onChange={(e) => setNewDonation({ ...newDonation, food_items: e.target.value })}
                        className="bg-[#F5F5F0] text-sm"
                      />
                      <Textarea
                        placeholder="Descriere (stare produse, detalii)"
                        value={newDonation.description}
                        onChange={(e) => setNewDonation({ ...newDonation, description: e.target.value })}
                        className="bg-[#F5F5F0] text-sm min-h-[80px]"
                      />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <Input
                          type="date"
                          placeholder="Data expirare"
                          value={newDonation.expiry_date}
                          onChange={(e) => setNewDonation({ ...newDonation, expiry_date: e.target.value })}
                          className="bg-[#F5F5F0] text-sm"
                        />
                        <Input
                          placeholder="Locație ridicare"
                          value={newDonation.pickup_location}
                          onChange={(e) => setNewDonation({ ...newDonation, pickup_location: e.target.value })}
                          className="bg-[#F5F5F0] text-sm"
                        />
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Button type="submit" disabled={loading} className="bg-[#1A4D2E] text-white text-sm">
                          {loading ? 'Se publică...' : 'Publică Donația'}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setShowDonationForm(false)} className="text-sm">
                          Anulează
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* Donations List */}
              {foodDonations.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {foodDonations.map((donation) => (
                    <Card key={donation.id} className="border-none card-shadow">
                      <CardContent className="p-4 sm:p-6">
                        <div className="flex items-start justify-between mb-3 gap-2">
                          <div className="min-w-0">
                            <h3 className="font-medium text-[#1A4D2E] text-base sm:text-lg truncate">{donation.title}</h3>
                            <Badge className={`text-xs mt-1 ${donation.status === 'available' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                              {donation.status === 'available' ? 'Disponibil' : 'Revendicat'}
                            </Badge>
                          </div>
                          <Package className="w-7 h-7 sm:w-8 sm:h-8 text-[#1A4D2E] flex-shrink-0" />
                        </div>
                        
                        {donation.description && (
                          <p className="text-sm text-[#4a5568] mb-3">{donation.description}</p>
                        )}
                        
                        {donation.food_items && (
                          <div className="flex flex-wrap gap-1 mb-3">
                            {(Array.isArray(donation.food_items)
                              ? donation.food_items
                              : String(donation.food_items).split(',').map(s => s.trim()).filter(Boolean)
                            ).map((item, i) => (
                              <Badge key={i} variant="outline" className="text-xs">{item}</Badge>
                            ))}
                          </div>
                        )}
                        
                        <div className="text-sm text-[#4a5568] space-y-1 mb-4">
                          {donation.quantity && <p>📦 Cantitate: {donation.quantity}</p>}
                          {donation.pickup_location && <p>📍 {donation.pickup_location}</p>}
                          {donation.expiry_date && <p>⏰ Valabil până: {donation.expiry_date}</p>}
                        </div>
                        
                        {donation.status === 'available' && (
                          <Button 
                            onClick={() => handleClaimDonation(donation.id)}
                            className="w-full bg-[#9BCF53] text-white hover:bg-[#8BC443]"
                          >
                            <Gift className="w-4 h-4 mr-2" />
                            Vreau această donație
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="border-none card-shadow">
                  <CardContent className="p-12 text-center">
                    <Gift className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" />
                    <p className="text-[#4a5568]">Nu sunt donații disponibile momentan</p>
                    <p className="text-sm text-[#4a5568] mt-2">Fii primul care donează!</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* NGOs Tab */}
            <TabsContent value="ngos">
              <div className="mb-6">
                <h2 className="font-serif text-xl sm:text-2xl text-[#1A4D2E]">ONG-uri Partenere</h2>
                <p className="text-[#4a5568] text-sm sm:text-base">Organizații care acceptă donații de alimente</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                {ngos.map((ngo) => (
                  <Card key={ngo.id} className="border-none card-shadow">
                    <CardContent className="p-4 sm:p-6">
                      <div className="flex flex-col sm:flex-row items-start gap-4">
                        <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#1A4D2E]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-7 h-7 sm:w-8 sm:h-8 text-[#1A4D2E]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-serif text-lg sm:text-xl text-[#1A4D2E] mb-1">{ngo.name}</h3>
                          <p className="text-sm text-[#4a5568] mb-3 line-clamp-2">{ngo.description}</p>
                          
                          <div className="flex flex-wrap gap-1 mb-3">
                            {ngo.city && (
                              <Badge className="bg-[#F5F5F0] text-[#4a5568] text-xs">
                                <MapPin className="w-3 h-3 mr-1" />
                                {ngo.city}
                              </Badge>
                            )}
                            {ngo.operating_hours && (
                              <Badge className="bg-[#F5F5F0] text-[#4a5568] text-xs">
                                <Clock className="w-3 h-3 mr-1" />
                                {ngo.operating_hours}
                              </Badge>
                            )}
                          </div>
                          
                          {ngo.food_types && ngo.food_types.length > 0 && (
                            <div className="mb-3">
                              <p className="text-xs text-[#4a5568] mb-1">Acceptă:</p>
                              <p className="text-sm text-[#1A4D2E] line-clamp-1">{ngo.food_types.join(', ')}</p>
                            </div>
                          )}
                          
                          <div className="flex flex-wrap gap-2">
                            {ngo.website && (
                              <a href={ngo.website} target="_blank" rel="noopener noreferrer">
                                <Button size="sm" variant="outline" className="border-[#1A4D2E] text-[#1A4D2E] text-xs">
                                  <Globe className="w-3 h-3 mr-1" />
                                  Website
                                </Button>
                              </a>
                            )}
                            {ngo.email && (
                              <a href={`mailto:${ngo.email}`}>
                                <Button size="sm" className="bg-[#1A4D2E] text-white text-xs">
                                  Contact
                                </Button>
                              </a>
                            )}
                            {ngo.phone && (
                              <a href={`tel:${ngo.phone}`}>
                                <Button size="sm" variant="outline" className="border-[#1A4D2E] text-[#1A4D2E] text-xs">
                                  <Phone className="w-3 h-3 mr-1" />
                                  {ngo.phone}
                                </Button>
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default Social;
